import { useEffect, useRef } from "react";

interface TurnstileApi {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  reset(widgetId: string): void;
  remove(widgetId: string): void;
}

declare global {
  interface Window { turnstile?: TurnstileApi }
}

let loader: Promise<TurnstileApi> | null = null;

function loadTurnstile(): Promise<TurnstileApi> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  if (loader) return loader;
  loader = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>('script[data-kleanbg-turnstile]');
    const script = existing ?? document.createElement("script");
    const ready = () => window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile did not initialize"));
    script.addEventListener("load", ready, { once: true });
    script.addEventListener("error", () => reject(new Error("Turnstile could not load")), { once: true });
    if (!existing) {
      script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
      script.async = true;
      script.defer = true;
      script.dataset.kleanbgTurnstile = "true";
      document.head.appendChild(script);
    }
  });
  return loader;
}

export function Turnstile({ onToken, resetKey }: { onToken(token: string): void; resetKey: number }) {
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;
  const containerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<string | null>(null);

  useEffect(() => {
    if (!siteKey || !containerRef.current) return;
    let active = true;
    void loadTurnstile().then((api) => {
      if (!active || !containerRef.current || widgetRef.current) return;
      widgetRef.current = api.render(containerRef.current, {
        sitekey: siteKey,
        theme: "auto",
        size: "flexible",
        callback: (token: string) => onToken(token),
        "expired-callback": () => onToken(""),
        "error-callback": () => onToken(""),
      });
    }).catch(() => onToken(""));
    return () => {
      active = false;
      if (widgetRef.current && window.turnstile) window.turnstile.remove(widgetRef.current);
      widgetRef.current = null;
    };
  }, [onToken, siteKey]);

  useEffect(() => {
    if (widgetRef.current && window.turnstile) window.turnstile.reset(widgetRef.current);
  }, [resetKey]);

  if (!siteKey) return null;
  return <div className="turnstile-wrap" ref={containerRef} aria-label="Human verification" />;
}
